/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  cloneElement,
  CSSProperties,
  memo,
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {createPortal} from 'react-dom';
import {translate} from '@ion/core/src/locale/locale';
import {IconButton} from '../components/buttons/IconButton';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import Render from '../render/Render';
import {dlsColor} from '../theme/theme';
import {IconSize} from '../types/types';
import {AppliedFilterBarItem} from './AppliedFilterBarTypes';

interface AppliedFilterOverflowGroup {
  id: string;
  items: AppliedFilterBarItem[];
  label?: string;
}

// space kept between the "+N More" trigger's bottom edge and the modal
// dropping below it ...
const ANCHOR_GAP_PX = 8;

export interface AppliedFilterOverflowModalProps {
  classes: Record<string, string>;
  items: AppliedFilterBarItem[];
  onClose: () => void;
  renderChip: (item: AppliedFilterBarItem) => ReactElement;
  anchorRef?: RefObject<HTMLElement>;
}

/**
 * Resolves the inline `top`/`right` style that drops the modal below
 * `anchorRef`'s current position, right-aligned to its right edge - or
 * `undefined` when there's no anchor, leaving the CSS class's own fixed
 * top-right position (Tracking) untouched.
 */
const getAnchoredStyle = (anchorRef?: RefObject<HTMLElement>): CSSProperties | undefined => {
  const anchorRect = anchorRef?.current?.getBoundingClientRect();
  return anchorRect ? {
    top   : anchorRect.bottom + ANCHOR_GAP_PX,
    right : globalThis.innerWidth - anchorRect.right
  } : undefined;
};

// grouped through a map, in the order each group is first met - a lookup
// per chip, rather than a search of every group so far ...
export const getAppliedFilterOverflowGroups = (
  items: AppliedFilterBarItem[]
): AppliedFilterOverflowGroup[] => (
  Array.from(items.reduce((groups, item) => {
    const groupId = item.groupLabel ?? item.id;
    const group   = groups.get(groupId);
    if (group) {
      group.items.push(item);
    } else {
      groups.set(groupId, {
        id    : groupId,
        items : [item],
        label : item.groupLabel
      });
    }
    return groups;
  }, new Map<string, AppliedFilterOverflowGroup>()).values())
);

/**
 * Displays overflowed applied filter chips in a scrim-backed modal.
 */
export const AppliedFilterOverflowModal = ({
  classes,
  items,
  onClose,
  renderChip,
  anchorRef
}: AppliedFilterOverflowModalProps) => {
  const [anchoredStyle, setAnchoredStyle] = useState<CSSProperties | undefined>(
    () => getAnchoredStyle(anchorRef)
  );

  const groups = useMemo(() => getAppliedFilterOverflowGroups(items), [items]);

  const renderModalChip = useCallback((item: AppliedFilterBarItem) => {
    const chip = renderChip(item);
    return cloneElement(chip, {
      className : `${chip.props.className} ${classes.modalChipGroupChip}`
    });
  }, [classes.modalChipGroupChip, renderChip]);

  useEffect(() => {
    if (anchorRef) {
      const onReposition = () => {
        setAnchoredStyle(getAnchoredStyle(anchorRef));
      };
      globalThis.addEventListener('resize', onReposition);
      return () => globalThis.removeEventListener('resize', onReposition);
    }
    return undefined;
  }, [anchorRef]);

  const content = (
    <>
      <div className={classes.scrim} onClick={onClose} />
      <div className={classes.overflowModal} style={anchoredStyle}>
        <div className={classes.modalHeader}>
          <span className={classes.modalTitle}>
            {translate('filters:selectedBar.moreTitle')}
          </span>
          <IconButton isControlButton
            icon={<MaterialExitCloseIcon color={dlsColor('grey1')} size={IconSize.small} />}
            onClick={onClose} />
        </div>
        <div className={classes.modalChips}>
          {groups.map((group, index) => (
            <div key={group.id}
              className={`${classes.modalChipGroup}${index ? ` ${classes.modalChipGroupSpacing}` : ''}`}>
              <Render.If condition={!!group.label}>
                <span className={classes.modalGroupLabel}>{group.label}</span>
              </Render.If>
              {group.items.map(renderModalChip)}
            </div>
          ))}
        </div>
      </div>
    </>
  );

  return globalThis.document?.body ? createPortal(content, globalThis.document.body) : content;
};

export default memo(AppliedFilterOverflowModal);
